import React from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { Users, TrendingUp, TrendingDown } from 'lucide-react'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

const UserTrendsChart = ({ data = {}, loading = false, title = "User Registration Trends" }) => {
  // Check if we have valid data from backend
  const hasData = data && (data.days?.length > 0 || data.labels?.length > 0)
  
  if (!hasData && !loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg mr-3">
              <Users className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600">No data available</p>
            </div>
          </div>
        </div>
        
        <div className="h-64 flex items-center justify-center">
          <div className="text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No user registration data available</p>
            <p className="text-xs text-gray-400 mt-1">
              Data will appear here once users start registering
            </p>
          </div>
        </div>
      </div>
    )
  }

  const chartData = data

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            family: 'Inter, sans-serif'
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#f97316',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (context) => `Date: ${context[0].label}`,
          label: (context) => `New Users: ${context.parsed.y}`
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        border: {
          display: false
        },
        ticks: {
          font: {
            size: 11,
            family: 'Inter, sans-serif'
          },
          color: '#6b7280'
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f3f4f6',
          drawBorder: false
        },
        border: {
          display: false
        },
        ticks: {
          font: {
            size: 11,
            family: 'Inter, sans-serif'
          },
          color: '#6b7280',
          stepSize: 5
        }
      }
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
        borderWidth: 2,
        hoverBorderWidth: 3
      },
      line: {
        tension: 0.4,
        borderWidth: 3
      }
    }
  }

  const chartDataConfig = {
    labels: chartData.days || chartData.labels,
    datasets: [
      {
        label: 'New Users',
        data: chartData.users || chartData.data,
        borderColor: '#f97316',
        backgroundColor: 'rgba(249, 115, 22, 0.1)',
        fill: true,
        pointBackgroundColor: '#f97316',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#ea580c',
        pointHoverBorderColor: '#fff'
      }
    ]
  }

  // Calculate trend
  const currentData = chartData.users || chartData.data || []
  if (currentData.length === 0) {
    return null // Don't render if no data
  }
  
  const lastWeekAvg = currentData.length >= 7 ? currentData.slice(-7).reduce((a, b) => a + b, 0) / 7 : 0
  const prevWeekAvg = currentData.length >= 14 ? currentData.slice(-14, -7).reduce((a, b) => a + b, 0) / 7 : 0
  const trend = lastWeekAvg > prevWeekAvg
  const trendPercentage = prevWeekAvg > 0 ? ((lastWeekAvg - prevWeekAvg) / prevWeekAvg * 100).toFixed(1) : 0

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 bg-gray-200 rounded w-48"></div>
            <div className="h-4 bg-gray-200 rounded w-20"></div>
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="p-2 bg-orange-100 rounded-lg mr-3">
            <Users className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">Last 14 days</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {trend ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
          <span className={`text-sm font-medium ${trend ? 'text-green-600' : 'text-red-600'}`}>
            {trend ? '+' : ''}{trendPercentage}%
          </span>
        </div>
      </div>
      
      <div className="h-64">
        <Line data={chartDataConfig} options={options} />
      </div>
      
      <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {currentData.reduce((a, b) => a + b, 0)}
          </div>
          <div className="text-xs text-gray-500">Total Users</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">
            {Math.round(lastWeekAvg)}
          </div>
          <div className="text-xs text-gray-500">Daily Average</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {Math.max(...currentData)}
          </div>
          <div className="text-xs text-gray-500">Peak Day</div>
        </div>
      </div>
    </div>
  )
}

export default UserTrendsChart
